import { ChangeEvent, Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Collapse,
	Divider,
	FormControl,
	Grid,
	IconButton,
	Input,
	InputAdornment,
	LinearProgress,
	Popover,
	Tooltip,
	Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FlakyIcon from "@mui/icons-material/Flaky";
import SentimentSatisfiedAltIcon from "@mui/icons-material/SentimentSatisfiedAlt";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import NavigationIcon from "@mui/icons-material/Navigation";
import { ReactComponent as ThumbDownIcon } from "@/Washouts/assets/icons/ThumbDownIcon.svg";
import { ReactComponent as ThumbUpIcon } from "@/Washouts/assets/icons/ThumbUpIcon.svg";
import { ReactComponent as EscalateIcon } from "@/Washouts/assets/icons/Escalate.svg";
import AttachedFilesList from "./attachedFilesList";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { IInput } from "@/Common/api/types/ChangeRequests";
import { faces, hands, miscellaneous } from "./emojis";

const MAX_FILES = 6;
const MAX_TOTAL_SIZE = 5242880; // 5MB
const allowedFileTypes = ["image/*", ".pdf", ".txt", ".doc", ".docx", ".xls", ".xlsx", ".ppt", ".pptx", ".csv", ".zip"];

const isValidFileType = (file: File) => {
	const fileExt = file.name.split(".").pop();
	const fileType = file.type;
	return allowedFileTypes.includes(`.${fileExt}`) || fileType.startsWith("image/");
};

type TChatInputProps = {
	alertMessage: string;
	setAlertMessage: (message: string) => void;
	openPopper: boolean;
	setOpenPopper: (open: boolean) => void;
	message: string;
	setMessage: Dispatch<SetStateAction<string>>;
	files: File[];
	setFiles: Dispatch<SetStateAction<File[]>>;
	hideEscalateAction: boolean;
	disableAllActions: boolean;
	disableStatusAction: boolean;
	statusActionTooltipText?: string;
	pending: { isPending: boolean; mutationAction: string };
	handleUpdate: (input: IInput, action: string, files?: File[]) => void;
};

const ChatInput = (props: TChatInputProps) => {
	const {
		alertMessage,
		setAlertMessage,
		openPopper,
		setOpenPopper,
		message,
		setMessage,
		files,
		setFiles,
		hideEscalateAction,
		disableAllActions,
		disableStatusAction,
		handleUpdate,
		pending,
		statusActionTooltipText,
	} = props;

	const [openEmojiMenu, setOpenEmojiMenu] = useState(false);

	const inputFileRef = useRef<HTMLInputElement | null>(null);
	const buttonRef = useRef<HTMLButtonElement | null>(null);
	const emojiRef = useRef<HTMLButtonElement | null>(null);

	const handleSend = async () => {
		if (!message.trim() && !files.length) {
			return;
		}
		const input: IInput = {
			chat: {
				message,
			},
		};
		handleUpdate(input, "SEND_MESSAGE", files);
		setMessage("");
		setFiles([]);
	};

	const triggerFileUploadWindow = () => inputFileRef.current?.click();

	const clearAlert = () => {
		setAlertMessage("");
		if (inputFileRef.current) {
			inputFileRef.current.value = "";
		}
	};

	// Validation function to check file limits
	const validateFiles = (prevFiles: File[], newFiles: File[]) => {
		// Filter out files that are already present
		const filteredNewFiles = newFiles.filter((file) => !prevFiles.some((prevFile) => prevFile.name === file.name));

		// Check if adding the new files would exceed the file count limit
		if (prevFiles.length + filteredNewFiles.length > MAX_FILES) {
			setAlertMessage("You cannot upload more than 6 files.");
			return prevFiles;
		}

		// Calculate the total size of the current files and the new files
		const totalSize = prevFiles.reduce((acc, file) => acc + file.size, 0) + filteredNewFiles.reduce((acc, file) => acc + file.size, 0);

		// Check if the total size exceeds the limit
		if (totalSize > MAX_TOTAL_SIZE) {
			setAlertMessage("The total size of the files exceeds the 5MB limit.");
			return prevFiles;
		}

		return [...prevFiles, ...filteredNewFiles];
	};

	const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
		if (event.target.files) {
			const selectedFiles = Array.from(event.target.files);
			setFiles((prevFiles) => validateFiles(prevFiles, selectedFiles));
		}
	};

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
		const clipboardItems = event.clipboardData?.items;
		if (inputFileRef.current && clipboardItems) {
			const newFiles: File[] = [];
			for (const item of clipboardItems) {
				const file = item.getAsFile();
				if (file && isValidFileType(file)) {
					// Check if the file is a screenshot
					if (file.type.startsWith("image/") && file.name === "image.png") {
						const timestamp = new Date().toISOString().replace(/T/g, " ").replace(/[:.Z]/g, "");
						const fileName = `Screenshot ${timestamp}.png`;
						const renamedFile = new File([file], fileName, { type: file.type });
						newFiles.push(renamedFile);
					} else {
						newFiles.push(file);
					}
				}
			}
			setFiles((prevFiles) => validateFiles(prevFiles, newFiles));
		}
	};

	const handleDrop = (event: React.DragEvent<HTMLInputElement>) => {
		event.preventDefault();
		const droppedFiles = Array.from(event.dataTransfer.files);
		const validFiles: File[] = [];

		if (inputFileRef.current && droppedFiles.length) {
			droppedFiles.forEach((file) => {
				if (isValidFileType(file)) {
					validFiles.push(file);
				}
			});

			const dataTransfer = new DataTransfer();
			validFiles.forEach((file) => dataTransfer.items.add(file));
			setFiles((prevFiles) => validateFiles(prevFiles, validFiles));
		}
	};

	const handleDragOver = (event: React.DragEvent<HTMLInputElement>) => {
		event.preventDefault();
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			handleSend();
		}
	};

	const handleOnChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		setMessage(event.target.value);
	};

	const handleClickAway = (event: MouseEvent | TouchEvent) => {
		if (buttonRef.current?.contains(event.target as Node)) {
			return;
		}
		setOpenPopper(false);
	};

	const handleChangeStatus = (status: string) => {
		const input: IInput = {
			status,
		};
		handleUpdate(input, "UPDATE_STATUS");
		setOpenPopper(false);
	};

	const handleEscalate = (escalate: boolean) => {
		const input: IInput = {
			supportAssigned: escalate,
		};
		handleUpdate(input, "UPDATE_SUPPORT_ASSIGNED");
		setOpenPopper(false);
	};

	const handleEmojiClick = (value: string) => {
		setMessage((prev) => prev + value);
		setOpenEmojiMenu(false);
	};

	const showLinearProgress =
		(pending.isPending && pending.mutationAction === "UPDATE_STATUS") ||
		(pending.isPending && pending.mutationAction === "UPDATE_SUPPORT_ASSIGNED");

	useEffect(() => {
		if (disableAllActions || disableStatusAction) {
			setOpenPopper(false);
		}
	}, [disableAllActions || disableStatusAction]);

	return (
		<Box>
			<ClickAwayListener onClickAway={clearAlert}>
				<Collapse in={!!alertMessage}>
					<Alert
						severity="error"
						className="!text-alert !font-sans !text-[14.4px]"
						sx={{ width: "515px", height: "50px" }}
						action={
							<IconButton aria-label="close" color="inherit" size="small" onClick={clearAlert}>
								<CloseIcon fontSize="inherit" />
							</IconButton>
						}
					>
						{alertMessage}
					</Alert>
				</Collapse>
			</ClickAwayListener>
			<ClickAwayListener onClickAway={handleClickAway}>
				<Collapse in={openPopper}>
					<Box
						p={2}
						sx={{
							width: 515,
							height: "65px",
							borderTop: "1px solid var(--table-hover)",
							boxShadow: "0 -2px 5px rgba(0, 0, 0, 0.1)",
						}}
					>
						<Grid container direction="row" spacing={2}>
							<Grid item>
								<Button
									id="select-plan-dialog-assign-btn"
									variant="contained"
									aria-controls="select-plan-dialog-assign-btn-menu"
									className="!bg-primary hover:!shadow-primary-shadow-50 h-[30px] rounded-[6px]"
									endIcon={<ThumbUpIcon />}
									onClick={() => handleChangeStatus("RESOLVED")}
								>
									<Typography className="change-request-btn-text">Resolve</Typography>
								</Button>
							</Grid>
							<Grid item>
								<Button
									id="commission-cycle-review-btn"
									variant="contained"
									aria-controls="select-plan-dialog-assign-btn-menu"
									className="h-[30px] rounded-[6px] !bg-gray-blue hover:!shadow-gray-blue"
									endIcon={<ThumbDownIcon />}
									onClick={() => handleChangeStatus("REJECTED")}
								>
									<Typography className="change-request-btn-text">Reject</Typography>
								</Button>
							</Grid>
							{!hideEscalateAction && (
								<Grid
									item
									sx={{ paddingLeft: "190px !important" }}
									alignItems="right"
									textAlign="right"
									alignContent="right"
								>
									<Button
										id="select-plan-dialog-assign-btn"
										variant="contained"
										aria-controls="select-plan-dialog-assign-btn-menu"
										className="!bg-primary hover:!shadow-primary-shadow-50 h-[30px] rounded-[6px]"
										endIcon={<EscalateIcon />}
										onClick={() => handleEscalate(true)}
									>
										<Typography className="change-request-btn-text">Escalate</Typography>
									</Button>
								</Grid>
							)}
						</Grid>
					</Box>
				</Collapse>
			</ClickAwayListener>
			{showLinearProgress && (
				<Box sx={{ width: "100%" }}>
					<LinearProgress />
				</Box>
			)}
			<Box
				className="rounded-bl-lg bg-table-hover !min-h-[50px] w-[100%] flex flex-row items-center"
				sx={{ borderTop: "1px solid #cccccc" }}
			>
				<Tooltip title={statusActionTooltipText} placement="top" arrow>
					<Box>
						<IconButton
							disabled={disableAllActions || disableStatusAction}
							ref={buttonRef}
							size="small"
							sx={{ fontSize: "35px", "&:hover": { backgroundColor: "transparent" } }}
							onClick={() => setOpenPopper(!openPopper)}
						>
							<FlakyIcon fontSize="inherit" sx={{ "&:hover": { color: "var(--primary-color)" } }} />
						</IconButton>
					</Box>
				</Tooltip>
				<FormControl>
					<Box className="!bg-white !rounded-[6px] !w-[420px] !min-h-[35px] my-[5px]">
						<Input
							disabled={disableAllActions}
							value={message}
							onChange={handleOnChange}
							onPaste={handlePaste}
							onDrop={handleDrop}
							onDragOver={handleDragOver}
							className="!w-full !rounded-[6px] !font-sans text-[14.5px] !font-[400] px-[5px] py-[1px]"
							disableUnderline
							type="text"
							onKeyDown={handleKeyDown}
							autoComplete="off"
							inputProps={{
								placeholder: "Type your message and press Enter to send",
								autoFocus: true,
							}}
							endAdornment={
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										sx={{ color: "var(--neutral-gray)", "&:hover": { backgroundColor: "transparent" } }}
										edge="end"
										size="small"
										disabled={disableAllActions}
										ref={emojiRef}
										onClick={() => setOpenEmojiMenu(true)}
									>
										<SentimentSatisfiedAltIcon />
									</IconButton>
									<IconButton
										aria-label="toggle password visibility"
										className="rotate-45"
										sx={{ color: "var(--neutral-gray)", "&:hover": { backgroundColor: "transparent" } }}
										edge="end"
										size="small"
										onClick={triggerFileUploadWindow}
										disabled={disableAllActions}
									>
										<AttachFileIcon />
									</IconButton>
									<input
										ref={inputFileRef}
										accept={allowedFileTypes.join()}
										style={{ display: "none" }}
										id="upload-files"
										type="file"
										multiple
										onChange={handleFileChange}
										disabled={disableAllActions}
									/>
								</InputAdornment>
							}
						/>
						<Popover
							open={openEmojiMenu}
							anchorEl={emojiRef.current}
							onClose={() => setOpenEmojiMenu(false)}
							anchorOrigin={{
								vertical: "top",
								horizontal: "center",
							}}
							transformOrigin={{
								vertical: "bottom",
								horizontal: "center",
							}}
						>
							<Box sx={{ p: 1, bgcolor: "background.paper", borderRadius: 2, display: "flex", flexDirection: "column" }}>
								<Box className="flex flex-wrap w-[220px]">
									{faces.map((face, index) => {
										const indexKey = `face-${index}`;
										return (
											<Box
												key={indexKey}
												className="relative hover:cursor-pointer hover:scale-150 hover:z-10"
												onClick={() => handleEmojiClick(face)}
											>
												{face}
											</Box>
										);
									})}
								</Box>
								<Divider className="!my-2" />
								<Box className="flex flex-wrap w-[220px]">
									{hands.map((hand, index) => {
										const indexKey = `hand-${index}`;
										return (
											<Box
												key={indexKey}
												className="relative hover:cursor-pointer hover:scale-150 hover:z-10"
												onClick={() => handleEmojiClick(hand)}
											>
												{hand}
											</Box>
										);
									})}
								</Box>
								<Divider className="!my-2" />
								<Box className="flex flex-wrap w-[220px]">
									{miscellaneous.map((misc, index) => {
										const indexKey = `misc-${index}`;
										return (
											<Box
												key={indexKey}
												className="relative hover:cursor-pointer hover:scale-150 hover:z-10"
												onClick={() => handleEmojiClick(misc)}
											>
												{misc}
											</Box>
										);
									})}
								</Box>
							</Box>
						</Popover>
						<AttachedFilesList fileList={files} setFileList={setFiles} />
					</Box>
				</FormControl>
				<IconButton
					disabled={disableAllActions}
					sx={{ backgroundColor: "white", fontSize: "25px" }}
					className="rotate-90 !mx-[5px]"
					size="small"
					onClick={handleSend}
				>
					<NavigationIcon fontSize="inherit" sx={{ color: "var(--primary-color)" }} />
				</IconButton>
			</Box>
		</Box>
	);
};

export default ChatInput;
